import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { matchPath, useNavigate } from "react-router-dom";
import { useSelector } from "../../common/hooks/useSelector";
import { useMultiplayer } from "../Multiplayer/useMultiplayer";
import { TeamContext } from "./TeamContext";
import { ROUTES } from "../../constant/common.constant";
import { isEmpty } from "lodash";
import { useAuth } from "../../common/hooks/useAuth";
import { TeamWithProjects, TeamWithProjectsAndDesigns } from "../../types";
import TeamService from "../../service/TeamService";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const TeamProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const path = window.location.pathname;
  const match = matchPath(`${ROUTES.TEAM}/*` as any, path);
  const teamId = match?.params?.teamId;
  const userInfos = useSelector((state) => state.userInfo);
  const [isTeamLoading, setIsTeamLoading] = useState(!!teamId);
  const [team, setTeam] = useState<TeamWithProjectsAndDesigns | undefined>(
    undefined
  );
  const [teams, setTeams] = useState<TeamWithProjects[]>([]);
  const [isTeamsLoading, setIsTeamsLoading] = useState(false);

  const { subscribeToTeam, unsubscribeFromTeam, isConnected } =
    useMultiplayer();

  const getTeam = useCallback(
    async ({ skipLoading = false }: { skipLoading?: boolean } = {}) => {
      if (!teamId) {
        return;
      }
      if (!skipLoading) {
        setIsTeamLoading(true);
      }
      try {
        const {
          data: { data },
        } = await TeamService.getTeamProjectsAndDesigns(teamId);
        setTeam(data);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          toast.warn(t("team.not_found"));
          navigate(ROUTES.HOME);
          return;
        } else {
          console.error(error);
        }
      } finally {
        setIsTeamLoading(false);
      }
    },
    [teamId, t, navigate]
  );

  //only used to display the teams in the left navigation
  const getTeams = useCallback(
    async ({ skipLoading = false }: { skipLoading?: boolean } = {}) => {
      if (!skipLoading) {
        setIsTeamsLoading(true);
      }
      const {
        data: { data: teams },
      } = await TeamService.getMyTeams();
      setTeams(teams);
      setIsTeamsLoading(false);
    },
    []
  );

  const addToTeam = useCallback(async (teamId: string) => {
    const {
      data: { data: newToken },
    } = await TeamService.getInviteLink(teamId);

    await TeamService.joinUserToTeamWithInviteToken(newToken);
  }, []);

  useEffect(() => {
    if (!teamId || !isConnected) return;
    subscribeToTeam(teamId);

    return () => {
      unsubscribeFromTeam();
    };
  }, [teamId, isConnected, subscribeToTeam, unsubscribeFromTeam]);

  useEffect(() => {
    if (!userInfos || isEmpty(userInfos) || !userInfos.id) {
      return;
    }
    getTeams({ skipLoading: true });
    return () => {
      setTeams([]);
    };
  }, [userInfos, isAdmin, getTeams]);

  return (
    <TeamContext.Provider
      value={{
        team,
        teams,
        isTeamLoading,
        isTeamsLoading,
        teamId,
        teammates: team?.teammates,
        refetchTeam: getTeam,
        refetchTeams: getTeams,
        addToTeam,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};
